import React from 'react';
import { getComparisonLeader } from './playerDashboardInsights';

const METRIC_CONFIG = {
  avgKD: { decimals: 2 },
  avgK: { decimals: 1 },
  avgD: { decimals: 1, lowerIsBetter: true, fewerLabel: true },
  avgA: { decimals: 1 },
  avgScore: { decimals: 0 },
  avgImpact: { decimals: 2 },
};

const PlayerComparisonModal = ({ players, onClose, playerStats, startDate, endDate, matchType, region, playerImages = {} }) => {
  const normalizeText = (value) => String(value || '').trim().toUpperCase();

  const withinDate = (row) => {
    if (startDate && row.Date && row.Date < startDate) return false;
    if (endDate && row.Date && row.Date > endDate) return false;
    return true;
  };

  const calculatePlayerStats = (playerName) => {
    const filtered = playerStats.filter(row => {
      if (row.Player !== playerName) return false;
      if (matchType && normalizeText(row.MatchType) !== normalizeText(matchType)) return false;
      if (region && normalizeText(row.Region) !== normalizeText(region)) return false;
      if (!withinDate(row)) return false;
      return true;
    });

    const totals = filtered.reduce((acc, s) => ({
      K: acc.K + Number(s.K || 0),
      D: acc.D + Number(s.D || 0),
      A: acc.A + Number(s.A || 0),
      Score: acc.Score + Number(s.Score || 0),
      Impact: acc.Impact + Number(s.Impact || 0),
      count: acc.count + 1
    }), { K: 0, D: 0, A: 0, Score: 0, Impact: 0, count: 0 });

    if (totals.count === 0) return null;

    return {
      games: totals.count,
      avgK: (totals.K / totals.count).toFixed(1),
      avgD: (totals.D / totals.count).toFixed(1),
      avgA: (totals.A / totals.count).toFixed(1),
      avgScore: Math.round(totals.Score / totals.count),
      avgImpact: (totals.Impact / totals.count).toFixed(2),
      avgKD: totals.D ? (totals.K / totals.D).toFixed(2) : totals.K.toFixed(2),
      totalK: totals.K,
      totalD: totals.D,
      totalA: totals.A
    };
  };

  const playerStatsList = players.map(player => ({
    name: player,
    stats: calculatePlayerStats(player)
  })).filter(p => p.stats !== null);

  const renderMetricCell = (player, metricKey) => {
    const value = player.stats[metricKey];
    const config = METRIC_CONFIG[metricKey] || { decimals: 1 };
    const current = Number(value || 0);
    let note = null;
    let noteClass = 'comparison-delta neutral';

    if (playerStatsList.length === 2) {
      const opponent = playerStatsList.find((item) => item.name !== player.name);
      const opponentValue = Number(opponent?.stats?.[metricKey] || 0);
      const diff = current - opponentValue;
      const absDiff = Math.abs(diff);
      const isEven = absDiff < 0.001;
      const currentLeads = config.lowerIsBetter ? diff < 0 : diff > 0;
      const formattedDiff = config.decimals === 0
        ? String(Math.round(absDiff))
        : absDiff.toFixed(config.decimals);

      if (isEven) {
        note = 'Even';
      } else if (currentLeads) {
        noteClass = 'comparison-delta positive';
        note = config.fewerLabel ? `${formattedDiff} fewer` : `+${formattedDiff}`;
      } else {
        note = config.fewerLabel
          ? `${opponent.name} ${formattedDiff} fewer`
          : `${opponent.name} +${formattedDiff}`;
      }
    } else if (playerStatsList.length >= 3) {
      const leader = getComparisonLeader(playerStatsList, metricKey, config.lowerIsBetter);
      if (leader === player.name) {
        note = 'Leader';
        noteClass = 'comparison-delta leader';
      }
    }

    return (
      <div className="comparison-metric-cell">
        <span>{value}</span>
        {note ? <small className={noteClass}>{note}</small> : null}
      </div>
    );
  };

  return (
    <div className="player-profile-modal-overlay" onClick={onClose}>
      <div className="player-profile-modal comparison-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Player Comparison</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-content">
          {playerStatsList.length === 0 ? (
            <div className="empty-state">
              <p>No statistics found for the selected players with the current filters.</p>
            </div>
          ) : (
            <div className="comparison-table-container">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Games</th>
                    <th>Avg K/D</th>
                    <th>Avg Kills</th>
                    <th>Avg Deaths</th>
                    <th>Avg Assists</th>
                    <th>Avg Score</th>
                    <th>Avg Impact</th>
                  </tr>
                </thead>
                <tbody>
                  {playerStatsList.map((player, idx) => (
                    <tr key={idx}>
                      <td className="player-name-compare">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {playerImages[player.name] && (
                            <img 
                              src={playerImages[player.name]} 
                              alt={player.name}
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: '2px solid #7E57C2'
                              }}
                            />
                          )}
                          <span>{player.name}</span>
                        </div>
                      </td>
                      <td>{player.stats.games}</td>
                      <td className={`${parseFloat(player.stats.avgKD) >= 1.2 ? 'performance-good' : parseFloat(player.stats.avgKD) >= 1.0 ? 'performance-average' : 'performance-poor'}`}>
                        {renderMetricCell(player, 'avgKD')}
                      </td>
                      <td>{renderMetricCell(player, 'avgK')}</td>
                      <td>{renderMetricCell(player, 'avgD')}</td>
                      <td>{renderMetricCell(player, 'avgA')}</td>
                      <td>{renderMetricCell(player, 'avgScore')}</td>
                      <td>{renderMetricCell(player, 'avgImpact')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayerComparisonModal;

